import React, { useEffect, useMemo, useState } from "react";
import { callAPI } from "../utils/apiUtils";
import { apiUrls } from "../utils/apiUrls";
import { toast } from "react-toastify";
import CustomerBookingHistoryModal from "./modals/CustomerBookingHistoryModal";
import "../styles/reservationcalnder.css";

const statuses = {
  0: "Reject",
  1: "Pending",
  2: "Accept",
  3: "Complete"
};

const TableView = () => {
  const [records, setRecords] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [historyMobile, setHistoryMobile] = useState(null);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const resp = await callAPI(apiUrls.RESERVATION_LISTING, {}, "GET");
      if (resp.data?.data) {
        setRecords(resp.data.data);
      } else {
        setRecords([]);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load records");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      const payload = { id, status };
      const resp = await callAPI(apiUrls.RESERVATION_UPDATE_STATUS, {}, "POST", payload, {});
      if (resp.status) {
        toast.success("Status updated");
        fetchRecords();
      } else {
        toast.error(resp.message || "Update failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Request failed");
    }
  };

  useEffect(() => {
    fetchRecords();
  }, []);

  const filtered = records.filter(r =>
    (`${r.first_name} ${r.last_name}`.toLowerCase().includes(search.toLowerCase()) ||
     r.email.toLowerCase().includes(search.toLowerCase()) ||
     r.mobile.includes(search))
  );

  // Records already come back latest-booking-first from the API, so grouping by mobile here
  // preserves that order within each customer's history — no separate fetch/sort needed.
  const bookingsByMobile = useMemo(() => {
    const map = {};
    records.forEach((r) => {
      if (!map[r.mobile]) map[r.mobile] = [];
      map[r.mobile].push(r);
    });
    return map;
  }, [records]);

  const historyBookings = historyMobile ? bookingsByMobile[historyMobile] || [] : [];

  return (
    <div>
      <div className="d-flex flex-wrap gap-3 align-items-center justify-content-between mb-4">
        <form className="formStyle searchStyle tabSearchInput mbFullWd">
          <svg width="17" height="18" className="searchIcon">
            <use xlinkHref="./images/sprite.svg#iconSearch"></use>
          </svg>
          <input
            type="text"
            className="form-control"
            placeholder="Search"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </form>
      </div>

      <div className="cardStyle">
        <div className="table-responsive">
          <table className="table tableStyle2 table-borderless align-middle">
            <thead>
              <tr>
                <th>S.no</th>
                <th>Name</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>Event</th>
                <th>Date Time</th>
                <th>Guest</th>
                <th>Message</th>
                <th>Settings</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="9">Loading...</td></tr>
              ) : (
                filtered.map((r, idx) => (
                  <tr key={r.id}>
                    <td>{idx + 1}</td>
                    <td>{r.first_name} {r.last_name}</td>
                    <td>{r.email}</td>
                    <td>
                      {r.mobile}
                      <button
                        type="button"
                        onClick={() => setHistoryMobile(r.mobile)}
                        title="View this customer's booking history"
                        className="bookingCountBadge"
                        style={{
                          marginLeft: "8px",
                          background: "#3a7ecf",
                          color: "#fff",
                          border: "none",
                          borderRadius: "12px",
                          padding: "3px 10px 3px 7px",
                          fontSize: "12px",
                          fontWeight: "bold",
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "5px",
                          boxShadow: "0 0 0 2px rgba(58, 126, 207, 0.25)",
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <path
                            d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                        </svg>
                        {bookingsByMobile[r.mobile]?.length ?? 1}
                      </button>
                    </td>
                    <td>{r.event}</td>
                    <td>{r.date} {r.time}</td>
                    <td>{r.guests}</td>
                    <td>{r.message}</td>
                    <td>
                      <select
                        className="form-select tableSelect"
                        value={r.status}
                        onChange={e => handleStatusChange(r.id, e.target.value)}
                      >
                        {Object.entries(statuses).map(([k, label]) => (
                          <option key={k} value={k}>{label}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>


        <nav className="mb-3" aria-label="Page navigation example">
          <ul className="pagination paginationStyle gap-2 justify-content-center">
            <li className="page-item disabled">
              <a className="pageLink" href="#!">
                <svg width="8" height="14" className="paginationArrow">
                  <use xlinkHref="./images/sprite.svg#iconLinkAngleLeft"></use>
                </svg>
              </a>
            </li>
            <li className="page-item"><a className="page-link primary" href="#!">1</a></li>
            <li className="page-item"><a className="page-link" href="#!">2</a></li>
            <li className="page-item">
              <a className="pageLink" href="#!">
                <svg width="8" height="14" className="paginationArrow">
                  <use xlinkHref="./images/sprite.svg#iconLinkAngleRight2"></use>
                </svg>
              </a>
            </li>
          </ul>
        </nav>

      </div>

      <CustomerBookingHistoryModal
        isOpen={!!historyMobile}
        onClose={() => setHistoryMobile(null)}
        mobile={historyMobile}
        bookings={historyBookings}
      />
    </div>
  );
};

export default TableView;